/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, ReactNode } from 'react';
import { Field, FormSpy } from 'react-final-form';
import { css, StyleSheet } from 'aphrodite';

import { BrandColors, Dropdown, TextInput, Validator } from '@blend/enterprise-components';

import { ScheduleClosingAppointmentTask } from '../ScheduleClosingAppointmentTask';

const styles = StyleSheet.create({
  formLabel: {
    color: BrandColors.NIGHT_90,
    fontSize: '12px',
    lineHeight: '18px',
  },
});

/**
 * Renders a container for a Text Input.
 * @param  {ScheduleClosingAppointmentTask.FormInputOptions} inputOptions  Input Options for Text Input
 * @return {ReactNode}                                                     Text Input container
 * @function
 */
export const renderFormTextInput = (inputOptions: ScheduleClosingAppointmentTask.FormInputOptions): ReactNode => {
  const inputProps: TextInput.Props = {
    disabled: inputOptions.disabled ?? false,
    id: inputOptions.name,
    locale: 'en_US',
    type: inputOptions.inputType ?? 'text',
  };

  let validationProp: Validator | undefined;
  if (inputOptions.validate) {
    validationProp = inputOptions.validate;
  }

  return (
    <>
      <FormSpy subscription={{ values: true }}>
        {({ values }) => (
          <Field defaultValue={values[inputOptions.name]} name={inputOptions.name} validate={validationProp}>
            {({ input, meta }) => {
              useEffect(() => {
                if (meta.error && meta.touched && !meta.validating) {
                  inputOptions.setErrorDispatch(meta.error);
                }
              }, [meta]);
              return (
                <>
                  <label className={css(styles.formLabel)} htmlFor={input.name}>
                    {inputOptions.label}
                  </label>
                  <TextInput {...input} {...inputProps} hasError={meta.error && meta.touched} />
                </>
              );
            }}
          </Field>
        )}
      </FormSpy>
    </>
  );
};

/**
 * Renders a container for a Select Input.
 * @param  {ScheduleClosingAppointmentTask.FormInputOptions} inputOptions Input Options for Select Input
 * @return {ReactNode}                                                    Select Input container
 * @function
 */
export const renderFormSelect = (inputOptions: ScheduleClosingAppointmentTask.FormInputOptions): ReactNode => {
  const inputProps: Dropdown.Props = {
    disabled: inputOptions.disabled ?? false,
    id: inputOptions.name,
    options: inputOptions.options ?? [],
  };

  if (inputOptions.valueRequired) {
    inputProps.valueRequired = inputOptions.valueRequired;
  } else {
    inputProps.placeholder = inputOptions.placeholder ?? '';
  }

  let validationProp: Validator | undefined;
  if (inputOptions.validate) {
    validationProp = inputOptions.validate;
  }

  return (
    <>
      <FormSpy subscription={{ values: true }}>
        {({ values }) => (
          <Field defaultValue={values[inputOptions.name]} name={inputOptions.name} validate={validationProp}>
            {({ input, meta }) => {
              useEffect(() => {
                if (meta.error && meta.touched && !meta.validating) {
                  inputOptions.setErrorDispatch(meta.error);
                }
              }, [meta]);
              return (
                <>
                  <label className={css(styles.formLabel)} htmlFor={input.name}>
                    {inputOptions.label}
                  </label>
                  <Dropdown {...input} {...inputProps} hasError={meta.error && meta.touched} />
                </>
              );
            }}
          </Field>
        )}
      </FormSpy>
    </>
  );
};
